import React, { useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import ClipLoader from "react-spinners/ClipLoader";
import OtpPin from "../../../components/OtpPin";
import { formatAmount } from "../../../utils/formatTime";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { API } from "../../../constants/api";
import useAuthAxios from "../../../utils/baseAxios";
import useIdempotencyKey from "../../../hooks/useIdempotencyKey";
import { errorMessage } from "../../../utils/errorMessage";
import { useUser } from "../../../context/user-context";
import { invalidateActivityQueries } from "../../../utils/invalidateActivityQueries";
import { NAIRA_WITHDRAWAL_FEE } from "../../../constants/constants";

type NairaWithdrawPreviewProps = {
  setOpenWithdraw: (open: boolean) => void;
  setOpenWithdrawPreview: (open: boolean) => void;
  setOpenWithdrawSuccess: (open: boolean) => void;
  amount: string;
  accountNumber: string;
  bankName: string;
  accountName: string;
};

const NairaWithdrawPreview: React.FC<NairaWithdrawPreviewProps> = ({
  setOpenWithdraw,
  setOpenWithdrawPreview,
  setOpenWithdrawSuccess,
  amount,
  accountNumber,
  bankName,
  accountName,
}) => {
  const queryClient = useQueryClient();
  const { refetch } = useUser();
  const [addPin, setAddPin] = useState(false);
  const [otp, setOtp] = useState("");

  const axiosInstance = useAuthAxios();
  const idempotency = useIdempotencyKey();

  const withdrawNaira = async (data: {
    accountNumber: string;
    amount: number;
    pin: string;
  }) => {
    const response = await axiosInstance.post(
      API.withdrawNairaWallet,
      data,
      idempotency.config()
    );
    return response.data;
  };

  const withdrawMutation = useMutation({
    mutationFn: withdrawNaira,
    onSuccess: (response) => {
      idempotency.rotate();
      toast.success(response?.message ?? "Withdrawal successful");
      invalidateActivityQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
      queryClient.invalidateQueries({ queryKey: ["naira-wallet-status"] });
      queryClient.invalidateQueries({ queryKey: ["naira-wallet"] });
      queryClient.invalidateQueries({
        queryKey: ["naira-wallet-transactions"],
      });
      refetch();
      setTimeout(() => {
        setOpenWithdrawPreview(false);
        setOpenWithdrawSuccess(true);
      }, 2000);
    },
    onError: (err) => {
      idempotency.rotateIfAnswered(err);
      const e = err as { message?: string; data?: { message?: string } };
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  return (
    <div className="fixed inset-0 z-[131] flex items-start justify-start overflow-auto bg-[#E7EBEE] pb-16 pt-16 font-manrope dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="mx-auto w-11/12 rounded-xl border bg-white p-6 dark:border-[#303030] dark:bg-[#192025] mds:w-8/12 lgss:w-2/5 xxl:w-1/3">
        <div className="flex w-full items-center justify-start gap-6">
          <button
            type="button"
            onClick={() => {
              setOpenWithdrawPreview(false);
              setOpenWithdraw(true);
            }}
            className="flex items-center gap-2"
          >
            <BsArrowLeft className="text-[16px] text-gray-800 dark:text-gray-200" />
          </button>
          <h4 className="font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
            Preview Withdraw
          </h4>
        </div>

        {addPin ? (
          <div className="mt-12">
            <h4 className="mb-4 text-center font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
              Enter transaction PIN
            </h4>
            <OtpPin otp={otp} input={4} setOtp={setOtp} />
            <button
              type="button"
              onClick={() => {
                withdrawMutation.mutate({
                  accountNumber,
                  amount: Number(amount),
                  pin: otp,
                });
              }}
              disabled={otp.length !== 4 || withdrawMutation.isPending}
              className={
                otp.length !== 4 || withdrawMutation.isPending
                  ? "mt-44 flex h-[52px] w-full items-center justify-center rounded-xl bg-gray-400 font-medium text-gray-200"
                  : "mt-44 flex h-[52px] w-full items-center justify-center rounded-xl bg-secondary_button font-medium text-white"
              }
            >
              {withdrawMutation.isPending ? (
                <ClipLoader color="#ffffff" size={20} />
              ) : (
                "Proceed"
              )}
            </button>
          </div>
        ) : (
          <div className="mt-8 w-full">
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Wallet
              </h4>
              <h4 className="mt-2 font-sora text-[20px] font-bold uppercase text-[#13181B] dark:text-gray-100">
                Naira (NGN)
              </h4>
            </div>
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Amount
              </h4>
              <h4 className="mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                ₦{formatAmount(Number(amount))}
              </h4>
            </div>
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Transfer fee
              </h4>
              <h4 className="mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                ₦{formatAmount(NAIRA_WITHDRAWAL_FEE)}
              </h4>
            </div>
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Total debit
              </h4>
              <h4 className="mt-2 text-[16px] font-semibold text-[#13181B] dark:text-gray-100">
                ₦{formatAmount(Number(amount) + NAIRA_WITHDRAWAL_FEE)}
              </h4>
            </div>
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Bank details
              </h4>
              <h4 className="mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                {accountNumber} - {bankName}
              </h4>
              <h4 className="mt-1 text-[14px] font-semibold text-[#BE6B0A]">
                {accountName}
              </h4>
            </div>
            <button
              type="button"
              onClick={() => setAddPin(true)}
              className="mt-12 flex h-[52px] w-full items-center justify-center rounded-xl bg-secondary_button text-[15px] font-medium text-white"
            >
              Withdraw NGN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NairaWithdrawPreview;
